import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import PlanComingSoonModal from "@/components/pricing/PlanComingSoonModal";

const TIER_IDS = ["spectator", "rookie", "amateur", "pro"] as const;
export type TierId = (typeof TIER_IDS)[number];

// Highlights shown on each tier card (values live in i18n). The free
// Spectator tier is read-only, so its card has no spec highlights.
const TIER_SPEC_KEYS: Record<TierId, readonly string[]> = {
  spectator: [],
  rookie: ["footage", "sessions", "length", "retention"],
  amateur: ["footage", "sessions", "length", "retention"],
  pro: ["footage", "sessions", "length", "retention"],
};

// Rows of the side-by-side comparison table.
const COMPARE_ROW_KEYS = [
  "footage",
  "footageTotal",
  "sessions",
  "sessionsTotal",
  "length",
  "retention",
  "replays",
  "live",
  "export",
  "download",
  "api",
  "seats",
  "fleet",
  "embed",
  "transcode",
  "support",
] as const;

const FAQ_KEYS = [
  "unit",
  "session",
  "replayVsOriginal",
  "expiry",
  "longStream",
] as const;

const TIER_META: Record<TierId, { featured: boolean; ctaClass: string }> = {
  spectator: { featured: false, ctaClass: "btn-secondary" },
  rookie: { featured: false, ctaClass: "btn-secondary" },
  amateur: { featured: true, ctaClass: "btn-primary" },
  pro: { featured: false, ctaClass: "btn-secondary" },
};

function TierCard({
  id,
  onSelect,
}: {
  id: TierId;
  onSelect?: () => void;
}) {
  const { t } = useTranslation("pages");
  const { featured, ctaClass } = TIER_META[id];
  return (
    <article
      className={`relative bg-white rounded-xl p-6 flex flex-col gap-4 ${
        featured
          ? "border border-primary-500 ring-1 ring-primary-500 shadow-sm"
          : "border border-secondary-200"
      }`}
    >
      {featured && (
        <span className="absolute -top-3 left-6 rounded-full bg-primary-500 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-white">
          {t("pricing.mostPopular")}
        </span>
      )}
      <div>
        <h3 className="text-lg font-bold tracking-tight">
          {t(`pricing.tiers.${id}.name`)}
        </h3>
        <p className="text-sm text-secondary-500 mt-1">
          {t(`pricing.tiers.${id}.tagline`)}
        </p>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-mono text-3xl font-bold tabular-nums tracking-tight text-secondary-900">
          {t(`pricing.tiers.${id}.price`)}
        </span>
        <span className="text-sm text-secondary-400">
          {t(`pricing.tiers.${id}.priceUnit`)}
        </span>
      </div>
      <ul className="flex flex-col gap-2.5 text-sm">
        {TIER_SPEC_KEYS[id].map((key) => (
          <li key={key} className="flex items-start gap-2">
            <Check className="h-4 w-4 mt-0.5 text-primary-600 shrink-0" />
            <span className="text-secondary-700">
              {t(`pricing.tiers.${id}.specs.${key}`)}
            </span>
          </li>
        ))}
      </ul>
      {onSelect ? (
        <button type="button" onClick={onSelect} className={`mt-auto ${ctaClass}`}>
          {t(`pricing.tiers.${id}.cta`)}
        </button>
      ) : (
        <Link to="/register" className={`mt-auto ${ctaClass}`}>
          {t(`pricing.tiers.${id}.cta`)}
        </Link>
      )}
    </article>
  );
}

function CompareTable() {
  const { t } = useTranslation("pages");
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] border-collapse text-sm">
        <thead>
          <tr>
            <th scope="col" className="text-left py-3 pr-4" />
            {TIER_IDS.map((id) => (
              <th
                key={id}
                scope="col"
                className={`text-left py-3 px-4 font-bold text-secondary-900 ${
                  TIER_META[id].featured ? "bg-primary-50 rounded-t-lg" : ""
                }`}
              >
                {t(`pricing.tiers.${id}.name`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {COMPARE_ROW_KEYS.map((row) => (
            <tr key={row} className="border-t border-secondary-200">
              <th
                scope="row"
                className="text-left font-medium py-3 pr-4 text-secondary-500"
              >
                {t(`pricing.compare.rows.${row}.label`)}
              </th>
              {TIER_IDS.map((id) => (
                <td
                  key={id}
                  className={`py-3 px-4 font-mono tabular-nums text-secondary-800 ${
                    TIER_META[id].featured ? "bg-primary-50" : ""
                  }`}
                >
                  {t(`pricing.compare.rows.${row}.${id}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PricingPage() {
  const { t } = useTranslation("pages");
  const [comingSoonTier, setComingSoonTier] = useState<TierId | null>(null);
  return (
    <div className="page-container pb-16">
      <div className="mb-6">
        <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-secondary-500 mb-1.5">
          {t("pricing.eyebrow")}
        </p>
        <h1 className="page-title">{t("pricing.title")}</h1>
        <p className="page-subtitle">{t("pricing.subtitle")}</p>
      </div>

      <section className="grid gap-5 md:grid-cols-3 lg:grid-cols-4">
        {TIER_IDS.map((id) => (
          <TierCard
            key={id}
            id={id}
            onSelect={
              id === "spectator" ? undefined : () => setComingSoonTier(id)
            }
          />
        ))}
      </section>
      <p className="mt-4 text-xs text-secondary-400">
        {t("pricing.placeholderNote")}
      </p>

      <PlanComingSoonModal
        isOpen={comingSoonTier !== null}
        tier={comingSoonTier}
        onClose={() => setComingSoonTier(null)}
      />

      <section className="mt-14">
        <p className="section-title">{t("pricing.compare.sectionTitle")}</p>
        <h3 className="text-xl font-bold tracking-tight mb-5">
          {t("pricing.compare.lead")}
        </h3>
        <CompareTable />
      </section>

      <section className="mt-14">
        <p className="section-title">{t("pricing.faq.sectionTitle")}</p>
        <h3 className="text-xl font-bold tracking-tight mb-5">
          {t("pricing.faq.lead")}
        </h3>
        <div className="grid gap-4 md:grid-cols-2">
          {FAQ_KEYS.map((k) => (
            <div
              key={k}
              className="bg-white border border-secondary-200 rounded-xl p-5"
            >
              <h4 className="font-bold mb-1.5">{t(`pricing.faq.${k}.q`)}</h4>
              <p className="text-sm text-secondary-500">
                {t(`pricing.faq.${k}.a`)}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
